import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete
} from "@mui/material";
import { CommentDto, TradingCounterParty } from "../../../../../../api/Api";
import {
  useAssignees,
  useCounterpartyProgressOverride,
  useUpdateCounterparty
} from "../../../../dataHooks/counterpartyQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../sharedComponents/commentsSection";
import { LifecycleDialog } from "../../sharedComponents/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificCounterparty: TradingCounterParty;
};

type FormData = {
  creditCheck: string;
  creditCheckAssignee: string;
  creditAmount: number | undefined;
  creditCurrency: string;
  comments: CommentDto[];
  signOffNote: string;
};

/** Split out of Risk: the credit check, its amount and currency. Worked by the Risk role. */
export const CreditRisk: FC<Props> = ({ specificCounterparty }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("counterparty");
  const roles = useRoles();
  const progressOverride = useCounterpartyProgressOverride(
    specificCounterparty,
    "creditRisk"
  );

  const creditRisk = specificCounterparty?.creditRiskLifecycle;
  const [formData, setFormData] = useState<FormData>({
    creditCheck: creditRisk?.creditCheck || "",
    creditCheckAssignee: creditRisk?.creditCheckAssignee || "",
    creditAmount: creditRisk?.creditAmount ?? undefined,
    creditCurrency: creditRisk?.creditCurrency || "",
    signOffNote: creditRisk?.signOffNote || "",
    comments: creditRisk?.comments || []
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const riskAssignees = useAssignees("risk");

  const updateCounterparty = useUpdateCounterparty({
    onSuccess: handleClose
  });
  const commentUpdate = useUpdateCounterparty();

  const buildCounterparty = (
    values: FormData & { complete?: boolean }
  ): TradingCounterParty => ({
    ...specificCounterparty,
    creditRiskLifecycle: {
      ...specificCounterparty.creditRiskLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildCounterparty({ ...formData, comments })
  });

  const handleChange = (
    field: keyof FormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificCounterparty.progress?.creditRisk,
    canSetInProgress: ownsLifecycle(roles, "creditrisk"),
    buildEntity: (values, progress) => {
      const saved = buildCounterparty(values);
      return {
        ...saved,
        progress: { ...saved.progress, creditRisk: progress }
      };
    },
    mutate: updateCounterparty.mutate
  });

  const open = location.pathname.endsWith("/creditrisk");

  // The credit amount is deliberately not required when approved: leaving it blank is how an
  // unlimited counterparty is recorded, matching M_CreditLimit NULL in the warehouse.
  const canComplete =
    (formData.creditCheck === "approved" && !!formData.creditCurrency) ||
    formData.creditCheck === "rejected";

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Credit Risk"
      progressOverride={progressOverride}
      maxWidth="md"
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete,
        isSaving: updateCounterparty.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl sx={{ flex: 1 }} size="medium" required>
            <InputLabel>Credit check</InputLabel>
            <Select
              label="Credit check"
              value={formData.creditCheck}
              onChange={(e) => handleChange("creditCheck", e.target.value)}
            >
              <MenuItem value="approved">
                <Typography variant="body1" component="span">
                  Approved
                </Typography>
              </MenuItem>
              <MenuItem value="rejected">
                <Typography variant="body1" component="span">
                  Rejected
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={riskAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              riskAssignees.data?.find(
                (a) => a.email === formData.creditCheckAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("creditCheckAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Credit check assignee"
                size="medium"
              />
            )}
          />
        </Box>

        {formData.creditCheck === "approved" && (
          <>
            <TextField
              label="Credit amount"
              type="number"
              value={formData.creditAmount ?? ""}
              onChange={(e) =>
                handleChange(
                  "creditAmount",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              helperText="Leave blank for no limit (unlimited)"
              slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
            />

            <TextField
              required
              label="Currency"
              value={formData.creditCurrency}
              onChange={(e) => handleChange("creditCurrency", e.target.value)}
              placeholder="e.g., USD, EUR, GBP"
              helperText="Required when credit check is approved"
            />
          </>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
